import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ServiceToggleCard } from "../components/ServiceToggleCard";
import { Shell } from "../components/Shell";
import { ApiError, tenants } from "../lib/api";
import type {
  PlatformConfig,
  SigningStatus,
  Tenant,
  TenantAIDraft,
  TenantSoporteRag,
} from "../lib/api";

/**
 * Hub central de servicios activables por tenant.
 *
 * Consolida en una sola vista todos los toggles que antes vivían
 * dispersos (Turnos en TenantDetail; Actos+Correspondencia en
 * /gestion-documental; AI Draft, Soporte RAG, Firma dentro de sus
 * páginas de config). El super-admin ve de un vistazo qué tiene la
 * alcaldía activo y puede saltar a "Configurar →" cuando necesita
 * ajustar parámetros.
 *
 * Los toggles individuales en cada página de detalle siguen existiendo
 * (decisión: convivir, no migrar de golpe).
 */
type LoadedState = {
  turnos: boolean;
  gdActos: boolean;
  gdCorrespondencia: boolean;
  gdExpedientes: boolean;
  aiDraft: TenantAIDraft;
  soporteRag: TenantSoporteRag;
  signing: SigningStatus;
  platform: PlatformConfig;
};

type BusyKey =
  | "turnos"
  | "actos"
  | "correspondencia"
  | "expedientes"
  | "ai_draft"
  | "soporte_rag"
  | "signing"
  | null;

export function TenantServiciosPage() {
  const { id } = useParams<{ id: string }>();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [state, setState] = useState<LoadedState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<BusyKey>(null);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  const flash = (kind: "ok" | "err", msg: string) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 2400);
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      tenants.get(id),
      tenants.getTurnos(id).catch(() => ({ enabled: false })),
      tenants.getGd(id),
      tenants.getAIDraft(id),
      tenants.getSoporteRag(id),
      tenants.getSigning(id),
      tenants.getPlatformConfig(),
    ])
      .then(([t, turnos, gd, aiDraft, soporteRag, signing, platform]) => {
        setTenant(t);
        setState({
          turnos: turnos.enabled,
          gdActos: gd.actos,
          gdCorrespondencia: gd.correspondencia,
          gdExpedientes: gd.expedientes,
          aiDraft,
          soporteRag,
          signing,
          platform,
        });
      })
      .catch((err) =>
        setError(err instanceof ApiError ? err.message : "No se pudo cargar"),
      )
      .finally(() => setLoading(false));
  }, [id]);

  const toggle = async (
    key: Exclude<BusyKey, null>,
    fn: () => Promise<Partial<LoadedState>>,
    label: string,
  ) => {
    if (!id || !state) return;
    setBusy(key);
    try {
      const patch = await fn();
      setState({ ...state, ...patch });
      flash("ok", label);
    } catch (err) {
      flash("err", err instanceof ApiError ? err.message : "Error al actualizar");
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <Shell>
        <section className="page">
          <div className="eyebrow">Cargando…</div>
        </section>
      </Shell>
    );
  }

  if (error || !tenant || !state) {
    return (
      <Shell>
        <section className="page">
          <div className="alert err">{error ?? "Tenant no encontrado."}</div>
          <p>
            <Link to="/tenants">← Volver</Link>
          </p>
        </section>
      </Shell>
    );
  }

  const slug = tenant.slug;
  const hasAIProviders =
    state.platform.providers.filter((p) => p.configured).length > 0;

  return (
    <Shell>
      <section className="page">
        <div className="eyebrow">
          <Link to={`/tenants/${tenant.id}`}>← {tenant.nombre}</Link> · Servicios
        </div>
        <h1 style={{ fontSize: 28, marginTop: 6 }}>Servicios de la alcaldía</h1>
        <p className="lede" style={{ maxWidth: 720 }}>
          Activa o desactiva los servicios que esta alcaldía tiene contratados.
          Cada servicio puede configurarse en detalle desde su propia página.
          Los toggles tienen efecto inmediato.
        </p>

        {/* Turnos */}
        <ServiceToggleCard
          eyebrow="Atención presencial"
          title="Turnos"
          description="Agendamiento de citas presenciales en taquilla. El admin de la alcaldía configura servicios, ventanillas y horarios; los ciudadanos toman turno desde el portal."
          enabled={state.turnos}
          busy={busy === "turnos"}
          onToggle={() =>
            void toggle(
              "turnos",
              async () => {
                const r = await tenants.setTurnos(id!, !state.turnos);
                return { turnos: r.enabled };
              },
              !state.turnos ? "Turnos activado" : "Turnos desactivado",
            )
          }
          urls={
            state.turnos
              ? [{ label: "Portal ciudadano", url: `${slug}.mipuebloapp.com/turnos` }]
              : []
          }
          capacidades={[
            "Catálogo de servicios y duración por servicio",
            "Múltiples ventanillas por dependencia",
            "Bloqueos por ventana horaria (almuerzo, festivos)",
            "Confirmación por WhatsApp y recordatorio 24h antes",
          ]}
        />

        {/* GD · Actos administrativos */}
        <ServiceToggleCard
          eyebrow="Gestión Documental · Ley 594/2000"
          title="Actos administrativos"
          description="Publicación de decretos, resoluciones, acuerdos y contratos con búsqueda full-text, workflow de aprobación, firma electrónica y portal público de transparencia (Ley 1712/2014)."
          enabled={state.gdActos}
          busy={busy === "actos"}
          onToggle={() =>
            void toggle(
              "actos",
              async () => {
                const cfg = await tenants.updateGd(id!, { actos: !state.gdActos });
                return {
                  gdActos: cfg.actos,
                  gdCorrespondencia: cfg.correspondencia,
                  gdExpedientes: cfg.expedientes,
                };
              },
              !state.gdActos ? "Actos activado" : "Actos desactivado",
            )
          }
          urls={
            state.gdActos
              ? [
                  { label: "Funcionarios", url: `${slug}.mipuebloapp.com/gd/actos` },
                  { label: "Portal ciudadano", url: `${slug}.mipuebloapp.com/documentos` },
                ]
              : []
          }
          capacidades={[
            "Numeración consecutiva atómica por tipo y año",
            "Búsqueda full-text en español ponderada",
            "Workflow: borrador → revisión → aprobado → publicado",
            "Versionado inmutable con motivo del cambio",
            "Tabla de Retención Documental (TRD) por tipo",
            "Portal público de transparencia (Ley 1712/2014)",
          ]}
        />

        {/* GD · Correspondencia */}
        <ServiceToggleCard
          eyebrow="Gestión Documental · Acuerdo AGN 060/2001"
          title="Radicación de correspondencia"
          description="Recepción y envío de oficios oficiales con sello digital, escaneo, acuse al remitente y asignación a dependencias. Pensado para ventanilla única."
          enabled={state.gdCorrespondencia}
          busy={busy === "correspondencia"}
          onToggle={() =>
            void toggle(
              "correspondencia",
              async () => {
                const cfg = await tenants.updateGd(id!, {
                  correspondencia: !state.gdCorrespondencia,
                });
                return {
                  gdActos: cfg.actos,
                  gdCorrespondencia: cfg.correspondencia,
                  gdExpedientes: cfg.expedientes,
                };
              },
              !state.gdCorrespondencia
                ? "Correspondencia activada"
                : "Correspondencia desactivada",
            )
          }
          urls={
            state.gdCorrespondencia
              ? [
                  {
                    label: "Ventanilla / funcionarios",
                    url: `${slug}.mipuebloapp.com/gd/correspondencia`,
                  },
                ]
              : []
          }
          capacidades={[
            "Consecutivos separados por tipo (CORR / OFI / MEM)",
            "Captura por escáner USB o cámara móvil",
            "Sello digital automático sobre el PDF al subir",
            "Acuse al remitente por WhatsApp + email con PDF estampado",
            "Asignación automática por categoría (tutelas → jurídica)",
            "Plazos legales tracking (tutela 48h, derecho de petición 15 días)",
          ]}
        />

        {/* GD · Expedientes */}
        <ServiceToggleCard
          eyebrow="Gestión Documental · Contenedores"
          title="Expedientes"
          description="Contenedores lógicos que agrupan actos, oficios y archivos sueltos bajo un mismo trámite o asunto (ej: contrato 045/2026, licencia urbanística 12, proceso sancionatorio X). Al activar se siembran 5 tipos por defecto con sus series AGN."
          enabled={state.gdExpedientes}
          busy={busy === "expedientes"}
          onToggle={() =>
            void toggle(
              "expedientes",
              async () => {
                const cfg = await tenants.updateGd(id!, {
                  expedientes: !state.gdExpedientes,
                });
                return {
                  gdActos: cfg.actos,
                  gdCorrespondencia: cfg.correspondencia,
                  gdExpedientes: cfg.expedientes,
                };
              },
              !state.gdExpedientes
                ? "Expedientes activado"
                : "Expedientes desactivado",
            )
          }
          urls={
            state.gdExpedientes
              ? [
                  {
                    label: "Funcionarios",
                    url: `${slug}.mipuebloapp.com/expedientes`,
                  },
                ]
              : []
          }
          capacidades={[
            "Numeración consecutiva por tipo y año (TENANT-PREFIJO-AAAA-#####)",
            "Tipos default: Genérico, Contractual, Urbanístico, Sancionatorio, Recurso",
            "Items polimórficos: actos + correspondencia + archivos sueltos",
            "Workflow: abierto → en trámite → cerrado → archivado",
            "FTS español sobre asunto, código, descripción y serie",
            "Series y subseries AGN + retención por tipo",
          ]}
        />

        {/* Bot RAG */}
        <ServiceToggleCard
          eyebrow="Inteligencia artificial · chat ciudadano"
          title="Bot RAG del chat"
          description="LLM que responde a los ciudadanos en el chat usando los artículos del centro de ayuda como contexto. Escala automáticamente a un operador si no encuentra la respuesta."
          enabled={state.soporteRag.enabled}
          busy={busy === "soporte_rag"}
          disabled={!hasAIProviders}
          disabledReason={
            <>
              No hay proveedores de IA configurados.{" "}
              <Link to="/platform/config">Configura al menos uno</Link> antes de activar.
            </>
          }
          onToggle={() =>
            void toggle(
              "soporte_rag",
              async () => {
                const c = await tenants.updateSoporteRag(id!, {
                  enabled: !state.soporteRag.enabled,
                });
                return { soporteRag: c };
              },
              !state.soporteRag.enabled ? "Bot RAG activado" : "Bot RAG desactivado",
            )
          }
          configurarPath={`/tenants/${tenant.id}/soporte-rag`}
          capacidades={[
            "Selección de proveedor IA (Claude / OpenAI / Gemini / local)",
            "Consentimiento Habeas Data para envío de PII (Ley 1581/2012)",
            "Escalamiento automático a operador humano",
            "Citado del artículo de KB usado en la respuesta",
          ]}
        />

        {/* AI Draft */}
        <ServiceToggleCard
          eyebrow="Inteligencia artificial · PQRSDF"
          title="Borrador automático de respuestas (AI Draft)"
          description="Asistente que genera un borrador formal de respuesta para cada PQRSDF conforme a Ley 1755/2015. El funcionario revisa, ajusta y firma."
          enabled={state.aiDraft.enabled}
          busy={busy === "ai_draft"}
          disabled={!hasAIProviders}
          disabledReason={
            <>
              No hay proveedores de IA configurados.{" "}
              <Link to="/platform/config">Configura al menos uno</Link> antes de activar.
            </>
          }
          onToggle={() =>
            void toggle(
              "ai_draft",
              async () => {
                const c = await tenants.updateAIDraft(id!, {
                  enabled: !state.aiDraft.enabled,
                });
                return { aiDraft: c };
              },
              !state.aiDraft.enabled ? "AI Draft activado" : "AI Draft desactivado",
            )
          }
          configurarPath={`/tenants/${tenant.id}/ai-draft`}
          capacidades={[
            "Selección de proveedor IA y modelo por alcaldía",
            "Plantillas formales acorde a Ley 1755/2015",
            "Solo borrador — el funcionario siempre firma",
          ]}
        />

        {/* Firma PAdES */}
        <ServiceToggleCard
          eyebrow="Certificado PKCS#12 · PAdES"
          title="Firma electrónica de PDFs"
          description="Firma automática de los PDFs de respuesta oficial con el certificado .p12 de la alcaldía, en formato PAdES (Ley 527/1999)."
          enabled={state.signing.enabled}
          busy={busy === "signing"}
          disabled={!state.signing.has_cert}
          disabledReason={
            <>
              No hay certificado cargado.{" "}
              <Link to={`/tenants/${tenant.id}/signing`}>Sube un .p12</Link> antes de activar.
            </>
          }
          onToggle={() =>
            void toggle(
              "signing",
              async () => {
                const s = await tenants.toggleSigning(id!, !state.signing.enabled);
                return { signing: s };
              },
              !state.signing.enabled ? "Firma activada" : "Firma desactivada",
            )
          }
          configurarPath={`/tenants/${tenant.id}/signing`}
          status={
            !state.signing.has_cert
              ? { label: "Sin certificado", tone: "warn" }
              : state.signing.enabled
                ? { label: "Firmando", tone: "ok" }
                : { label: "Inactivo", tone: "muted" }
          }
          capacidades={[
            "Firma PAdES sobre todos los PDFs de respuesta",
            "Contraseña del .p12 cifrada con KEK global de plataforma",
            "Trazabilidad del sujeto y vencimiento del certificado",
          ]}
        />

        {toast && (
          <div
            className={`toast ${toast.kind}`}
            role="status"
            aria-live="polite"
          >
            {toast.msg}
          </div>
        )}
      </section>
    </Shell>
  );
}
